//Library Imports
import React, { useState, useEffect } from 'react';
import store, { RootState } from 'store';
import { useDispatch, useSelector } from 'react-redux';
import { Radio, Tooltip, Typography, Collapse, Row, Col, Select, Spin } from 'antd';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

//Local Imports
import xiqPlaceholderImage from '../../../assets/img/xiqPlaceholder.jpg';
import { ReactComponent as Dropuparrow } from '../../../assets/img/icons/dropuparrow.svg';
import DownAngle from '../../../assets/img/icons/DownAngle.svg';
import EyeGreenIcon from '../../../assets/img/icons/EyeGreen.svg';
import EditIcon from '../../../assets/img/icons/EditIcon.svg';
import Input from '../../Common/Components/Input';
import { IEmailData, ISenderEmail } from '../interfaces';
import { removeOnClickBubble } from 'utils';
import { AUTOMATION_EMAILS } from 'constants/types/automationTypes';
import { showUpdateButton } from '../actions';
import { DESIGN } from 'constants/actionTypes';

type Props = {
  emailName: string;
  subject: string;
  sendFrom: string;
  senderName: string;
  thumbnail: string;
  date: string;
  id: number;
  isFormValid: () => any;
  designReady: boolean;
  versionEmailType: number;
  campaignId: number;
  campaignName: string;
  senderEmails: ISenderEmail[];
  enableSaveButton: (val: boolean) => void;
  isActionModal?: boolean;
  setErrorFields?: any;
  selectedEmailId?: number;
  loadingEmailUpdate?: boolean;
  onShowPreview?: (id: number, campaignId: number) => void;
  emailPatform?: string;
  setIsChangedOutlook: (val: boolean) => void;
};

const EmailItem = (props: Props) => {
  // Props
  const {
    id,
    emailName,
    sendFrom,
    date,
    thumbnail,
    campaignId,
    campaignName,
    isActionModal,
    senderEmails,
    selectedEmailId,
    loadingEmailUpdate,
    emailPatform,
    isFormValid,
    enableSaveButton,
    setIsChangedOutlook,
  } = props;

  // Redux States
  const workflowId = useSelector((state: RootState) => state.automation.workflow.detail.id);
  const emailForm = useSelector((state: RootState) => state.automation.emails.form);
  const currentData = useSelector((state: RootState) => state.automation.emails.form.currentData);
  const emails: IEmailData[] = useSelector((state: RootState) => state.automation.emails.data);
  const formError = emailForm?.errors;
  const emailsData: IEmailData[] = emails;
  const { first_name, last_name } = useSelector((state: RootState) => state.user.userData);

  // Local States
  const [ellipsisState, setEllipsisState] = useState(false);
  const [activeKey, setActiveKey] = useState(['-1']);

  // Local Variables
  const { Panel } = Collapse;
  const dispatch = useDispatch();
  const { Paragraph } = Typography;
  const fullName = first_name + ' ' + last_name;
  const anyFieldDiffers =
    currentData.senderName !== emailForm.senderName ||
    currentData.sendFrom !== emailForm.sendFrom ||
    currentData.emailSubject !== emailForm.emailSubject;
  const allFieldsUnchanged =
    currentData.senderName === emailForm.senderName &&
    currentData.sendFrom === emailForm.sendFrom &&
    currentData.emailSubject === emailForm.emailSubject;
  const error = isActionModal && (formError.emailSubject || formError.sendFrom || (formError.senderName ?? fullName)) && selectedEmailId === id;

  const handleChangeForm = (e: React.ChangeEvent<HTMLInputElement>) => {
    let id, value;
    if (e?.target?.id) {
      id = e.target.id;
      // if user try to save empty field
      if (e.target.value.trim() === '') {
        value = '';
      } else {
        value = e.target.value;
      }
    } else {
      if (emailPatform === '3') {
        setIsChangedOutlook(true);
      }
      id = 'sendFrom';
      value = e;
    }

    store.dispatch({
      type: AUTOMATION_EMAILS.UPDATE_FORM,
      payload: {
        name: id,
        value,
        error: !value,
      },
    });
  };

  useEffect(() => {
    let email = emailsData.filter((email) => email.emailId === selectedEmailId);
    if (selectedEmailId === id) {
      let defaultSenderEmail = '';
      if (!email[0]?.sendFrom) {
        senderEmails.forEach((email: any) => {
          if (email.is_default) {
            defaultSenderEmail = email.domain;
          }
        });
      }
      // Sets form data
      store.dispatch({
        type: AUTOMATION_EMAILS.SET_FORM,
        payload: {
          emailId: id,
          emailSubject: email[0]?.subject,
          senderName: email[0]?.senderName ?? fullName,
          sendFrom: !defaultSenderEmail ? email[0]?.sendFrom : defaultSenderEmail,
          versionEmailType: email[0]?.versionEmailType,
          designReady: email[0]?.designReady,
          campaignId: email[0]?.campaignId,
          errors: {
            emailSubject: !email[0]?.subject,
            senderName: !email[0]?.senderName !== false ? !fullName : !email[0]?.senderName,
            sendFrom: !email[0]?.sendFrom,
          },
        },
      });
      if (defaultSenderEmail) {
        store.dispatch({
          type: AUTOMATION_EMAILS.UPDATE_FORM,
          payload: {
            name: 'sendFrom',
            value: defaultSenderEmail,
            error: false,
          },
        });
      }
    }
  }, [selectedEmailId]);

  useEffect(() => {
    // Triggers on any of the fields' value change
    if (selectedEmailId === id) {
      if (anyFieldDiffers) {
        if (error) {
          showUpdateButton(false);
        } else {
          showUpdateButton(true);
          enableSaveButton && enableSaveButton(true);
        }
      }
      if (allFieldsUnchanged) {
        enableSaveButton && enableSaveButton(false);
        if (!error) {
          showUpdateButton(false);
        } else {
          showUpdateButton(true);
        }
      }
    }
  }, [emailForm.senderName, emailForm.sendFrom, emailForm.emailSubject]);

  useEffect(() => {
    // Triggers on error value change
    if (selectedEmailId === id) {
      if (error === true) {
        showUpdateButton(true);
        enableSaveButton && enableSaveButton(false);
      } else {
        // if error === false
        if (anyFieldDiffers) {
          showUpdateButton(true);
        } else {
          showUpdateButton(false);
        }
      }
    }
  }, [error]);

  useEffect(() => {
    // if view details opened and user select another email, it should collapse the current item
    if (selectedEmailId !== id) {
      setActiveKey(['-1']);
    } else if (selectedEmailId === id) {
      isFormValid && isFormValid();
      setActiveKey(['1']);
    }
  }, [selectedEmailId]);

  const onChangeKey = (key: any) => {
    setActiveKey(key);
  };

  return (
    <WrapperOuter error={error}>
      {/* switch class "invalid" */}
      <Wrapper className="d-flex">
        <div className="checkbox align-self-center">
          <Radio value={id} />
        </div>
        <div
          className="thumbnail"
          style={{
            backgroundImage: `url(${thumbnail && thumbnail?.length > 0 ? thumbnail : xiqPlaceholderImage})`,
          }}
        ></div>
        <div className="content-area d-flex flex-column align-items-start align-self-center">
          <div className="name">{campaignName}</div>
          <div className="subject">
            <Tooltip placement="top" title={ellipsisState ? props?.emailName : undefined}>
              <Paragraph className="camp-name" ellipsis={{ onEllipsis: setEllipsisState }}>
                {emailName}
              </Paragraph>
            </Tooltip>
          </div>
          {sendFrom && sendFrom.length > 0 && (
            <div className="sendfrom">
              Send from: <span>{sendFrom}</span>
            </div>
          )}
        </div>
        <div className="date align-self-start">
          {isActionModal && (
            <div className={`date-label sended`}>
              <div
                className="eye-view"
                onClick={(e) => {
                  e.stopPropagation();
                  removeOnClickBubble(e);
                  if (!isActionModal) return;
                  props.onShowPreview && props.onShowPreview(id, campaignId);
                }}
              >
                <img src={EyeGreenIcon} alt="eye" />
                Preview
              </div>
              <Link
                className="edit-btn"
                onClick={() => dispatch({ type: DESIGN.RESET_DESIGN })}
                to={`/campaigns/editionedit/design/?campaign_id=${campaignId}&email_id=${id}&from=workflow&workflow_id=${workflowId}`}
              >
                <img src={EditIcon} alt="eye" /> Edit Email
              </Link>
            </div>
          )}
          <span>{date}</span>
          <div className="edit-sender-detail">View Details</div>
        </div>
      </Wrapper>
      {isActionModal && (
        <WrapperCollapse>
          {loadingEmailUpdate && selectedEmailId === id && (
            <LoadingWrapper>
              <Spin />
            </LoadingWrapper>
          )}
          <Collapse activeKey={activeKey} onChange={onChangeKey} ghost={true} expandIcon={({ isActive }) => <Dropuparrow />}>
            <Panel header="View Details " key={'1'} showArrow={true}>
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Input
                    idInput="senderName"
                    label="Sender Name:*"
                    type="text"
                    className={formError.senderName ? 'invalid' : ''}
                    onChange={handleChangeForm}
                    value={emailForm.senderName}
                    placeholder="Enter sender name…"
                  />
                </Col>
                <Col span={12}>
                  <div className="input-box">
                    <label className="publish-label">Send From: *</label>
                    <Select
                      id="sendFrom"
                      placeholder="Select Email"
                      style={{ width: '100%' }}
                      className={formError.sendFrom ? 'error-select-publish' : ''}
                      onChange={handleChangeForm}
                      value={emailForm.sendFrom}
                      listHeight={140}
                      options={senderEmails.map((item: ISenderEmail) => ({
                        label: item.domain,
                        value: item.domain,
                      }))}
                    />
                  </div>
                </Col>
                <Col span={24}>
                  <Input
                    idInput="emailSubject"
                    label="Email Subject:*"
                    type="text"
                    className={formError.emailSubject ? 'invalid' : ''}
                    onChange={handleChangeForm}
                    value={emailForm.emailSubject}
                    placeholder="Enter a subject..."
                  />
                </Col>
              </Row>
              <div className="footer-note">
                <span className="notic">
                  <b className={formError.emailSubject || formError.sendFrom || formError.senderName ? 'color-red' : ''}>Note:</b> These fields are
                  mandatory to fill.
                </span>
              </div>
            </Panel>
          </Collapse>
        </WrapperCollapse>
      )}
    </WrapperOuter>
  );
};

export default EmailItem;

const WrapperOuter = styled.div<{ error?: boolean }>`
  border-radius: 6px;
  box-shadow: 0px 0px 17px -2px rgba(0, 0, 0, 0.08);
  background-color: #fff;
  border: ${(props) => (props?.error ? '1px solid #ff0000' : '1px solid #fff')};
  margin: 0 16px 8px 16px;
  margin: 0 16px 8px 16px;
  @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
    border-radius: 5px;
  }
  &.invalid {
    border: 1px solid #ff0000;
  }
`;

const WrapperCollapse = styled.div`
  position: relative;
  .update-btn {
    padding: 5px 10px;
    font-size: 14px;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      font-size: 10px;
    }
  }
  .ant-collapse-header {
    position: absolute !important;
    right: 35px;
    top: -25px;
    padding: 0 !important;
    text-align: right;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      right: 13px;
    }
    .ant-collapse-expand-icon {
      float: right;
      padding-left: 5px;
      svg {
        height: 24px !important;
        padding: 0 !important;
        position: static !important;
      }
    }
    .ant-collapse-header-text {
      font-family: 'Roboto', sans-serif;
      font-weight: 500;
      font-size: 11px;
      color: #1ec252;
    }
  }
  .ant-collapse-item-active {
    .ant-collapse-expand-icon {
      transform: rotate(180deg);
      padding-left: 0px;
      padding-right: 5px;
      svg {
        height: 16px !important;
      }
    }
  }
  .ant-collapse-content {
    padding: 4px 35px 0 55px;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      padding: 2px 12px 0 40px;
    }
    .ant-collapse-content-box {
      border-top: 1px solid #dddddd;
      padding: 12px 0 10px 0 !important;
      .footer-note {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-top: 6px;
      }
      .notic {
        font-weight: 500;
        font-size: 11px;
        color: #67696a;
        font-family: 'Roboto', sans-serif;
        b {
          font-weight: 700;
        }
        b.color-red {
          color: #ff0000;
        }
        a {
          color: #1ec252;
          margin-left: 2px;
        }
      }
      label {
        margin-top: 0;
      }
      .invalid {
        color: #424b50 !important;
        -webkit-text-fill-color: #424b50 !important;
      }
      .input-box {
        .ant-radio-group {
          margin-bottom: 16px;
          label {
            margin-right: 50px;
            .ant-radio-inner {
              border-color: #b0b0b0;
              &:after {
                background-color: #b0b0b0;
              }
            }
          }
          span {
            color: #5e7079;
            font-size: 16px;
            font-weight: 500;
            font-family: 'robotomedium';
            @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
              font-size: 12px;
            }
          }
        }
        .same-email {
          color: #5e7079;
          font-size: 16px;
          font-weight: 500;
          font-family: 'robotomedium';
          margin-top: 10px;
          cursor: pointer;
          margin-bottom: -8px;
          float: left;
          width: 100%;
          @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
            line-height: 18px;
            font-size: 12px;
          }
        }
        .publish-label {
          color: #5e7079;
          font-size: 18px;
          font-family: 'Roboto Slab', serif;
          padding-bottom: 8px;
          font-weight: 600;
          @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
            font-size: 14px;
            padding-bottom: 6px;
          }
        }
        .ant-select-selector {
          padding: 7px 15px;
          height: auto;
          @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
            padding: 7px 12px;
            border-radius: 6px;
            font-size: 12px;
            margin-top: 2px;
            line-height: 16px;
            .ant-select-selection-placeholder {
              line-height: inherit;
            }
          }
          .ant-select-selection-item {
            color: #7e7e7e;
            font-size: 16px;
            font-weight: 600;
            font-family: 'robotoregular';
            @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
              font-size: 12px;
              line-height: 16px;
            }
          }
          &.error-select {
            border: 2px solid #ffa112 !important;
          }
          &:after {
            @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
              line-height: 14px;
            }
          }
        }
        .error-select .ant-select-selector {
          border: 1px solid #ffa112 !important;
          box-shadow: none !important;
        }
        .error-select-publish .ant-select-selector {
          border: 1px solid #db3725 !important;
        }
        .ant-select-arrow {
          .anticon {
            background: url(${DownAngle});
            background-repeat: no-repeat;
            background-position: center;
            svg {
              opacity: 0;
            }
          }
          &:before {
            margin-top: -4px;
            content: '' !important;
            border-left: 1px solid #dddddd;
            display: block;
            height: 22px;
            margin-right: 8px;
            margin-left: -12px;
            @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
              margin-top: -2px;
              height: 18px;
            }
          }
        }
      }
    }
  }
`;

const LoadingWrapper = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1;
  background: rgba(220, 220, 220, 0.3);
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Wrapper = styled.div`
  background-color: #fff;
  border-radius: 7px;
  padding: 12px 16px;
  @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
    border-radius: 5px;
    padding: 8px 10px;
  }
  .thumbnail {
    width: 78px;
    height: 65px;
    display: block;
    border-radius: 6px;
    background-repeat: no-repeat;
    background-size: cover;
    margin: 0 13px;
    position: relative;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      margin: 0 7px;
      width: 58px;
      height: 50px;
      border-radius: 4px;
    }
  }
  .content-area {
    width: calc(100% - 265px);
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      width: calc(100% - 205px);
    }
    .name {
      color: #67696a;
      font-size: 10px;
      font-weight: 500;
      font-family: 'Roboto', sans-serif;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      width: 100%;
      padding-right: 70px;
    }
    .subject {
      color: #67696a;
      font-size: 16px;
      font-weight: 500;
      font-family: 'Roboto', sans-serif;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      width: 100%;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        font-size: 13px;
      }
      .camp-name {
        max-width: 300px;
        margin-bottom: 0;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          max-width: 400px;
        }
      }
    }
    .sendfrom {
      color: #67696a;
      font-size: 10px;
      font-weight: 400;
      font-family: 'Roboto', sans-serif;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      width: 100%;
      span {
        color: #1ec252;
        font-weight: 500;
      }
    }
  }
  .date {
    margin-left: 15px;
    padding-top: 4px;
    .date-label {
      font-size: 11px;
      font-weight: 500;
      font-family: 'Roboto', sans-serif;
      text-align: right;
      margin-bottom: 4px;
      line-height: 12px;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        font-size: 10px;
      }
      &.sended {
        position: relative;
        padding: 0;
        color: #1ec252;
        background: transparent;
        text-decoration-line: none;
        font-size: 12px;
        line-height: 12px;
        letter-spacing: -0.275px;
        .eye-view {
          width: 25px;
          height: 16px;
          position: absolute;
          top: -4px;
          left: -40px;
          background-color: #fff;
          border-radius: 22px;
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          padding-right: 7px;
          min-width: 65px;
          cursor: pointer;
          font-family: 'Roboto', sans-serif;
          font-size: 12px;
          line-height: 1;
          letter-spacing: -0.275px;
          color: #1ec252;
          @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
            left: -50px;
          }
          img {
            width: 14px;
            height: 14px;
          }
        }
        .edit-btn,
        .edit-sender-detail {
          color: #1ec252;
        }
      }
      &.scheduled {
        color: #eb7719;
      }
    }
    span {
      color: #67696a;
      font-size: 10px;
      font-weight: 400;
      line-height: 13px;
      font-family: 'Roboto', sans-serif;
      text-align: right;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        font-size: 9px;
      }
    }
  }
`;
